import React, { useState, useEffect } from 'react';
import { Stage, Layer, Line, Rect, Text, Circle, Image as KonvaImage, Group } from 'react-konva';
import ApplianceElement from './sketch/ApplianceElement';

const SketchCanvasViewer = ({ page, width = 1200, height = 800 }) => {
  const [bgImage, setBgImage] = useState(null);

  useEffect(() => {
    if (!page?.backgroundImage) {
      setBgImage(null);
      return;
    }

    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setBgImage(img);
    img.src = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${page.backgroundImage}`;
  }, [page?.backgroundImage]);

  if (!page) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        Nenhuma página selecionada
      </div>
    );
  }

  const allVisibleElements = page.layers
    ?.filter(layer => layer.visible !== false)
    ?.flatMap(layer => layer.elements || []) || [];

  const renderElement = (element, index) => {
    switch (element.type) {
      case 'rectangle':
        return (
          <Rect
            key={element.id || index}
            x={element.x}
            y={element.y}
            width={element.width}
            height={element.height}
            stroke={element.stroke || '#2563eb'}
            strokeWidth={element.strokeWidth || 2}
            fill={element.fill || 'transparent'}
          />
        );

      case 'line':
        const [x1, y1, x2, y2] = element.points || [];
        return (
          <React.Fragment key={element.id || index}>
            <Line
              points={element.points}
              stroke={element.color || '#2563eb'}
              strokeWidth={element.strokeWidth || 4}
              hitStrokeWidth={40}
            />
            {element.measurements?.length && (
              <Text
                text={`${Math.round(element.measurements.length)}cm`}
                x={(x1 + x2) / 2 - 25}
                y={(y1 + y2) / 2 - 25}
                fontSize={12}
                fill="#6b7280"
                listening={false}
              />
            )}
          </React.Fragment>
        );

      case 'freehand':
        return (
          <Line
            key={element.id || index}
            points={element.points}
            stroke={element.color || '#2563eb'}
            strokeWidth={element.strokeWidth || 2}
            tension={0.5}
            lineCap="round"
            lineJoin="round"
          />
        );

      case 'text':
        return (
          <Text
            key={element.id || index}
            x={element.x}
            y={element.y}
            text={element.text}
            fontSize={element.fontSize || 16}
            fill={element.color || '#374151'}
          />
        );

      case 'circle':
        return (
          <Circle
            key={element.id || index}
            x={element.x}
            y={element.y}
            radius={element.radius}
            stroke={element.stroke || '#2563eb'}
            strokeWidth={element.strokeWidth || 2}
            fill={element.fill || 'transparent'}
          />
        );

      case 'door_arc':
        const dw = element.width || 80;
        const drot = element.rotation || 0;
        const dflip = element.flipped || false;
        return (
          <Group key={element.id || index} x={element.x} y={element.y} rotation={drot} scaleX={dflip ? -1 : 1}>
            <Line points={[0, 0, 0, -dw]} stroke="#2563eb" strokeWidth={2.5} />
            <Line points={[0, 0, dw, 0]} stroke="#94a3b8" strokeWidth={1.5} dash={[3, 3]} />
            <Line
              points={(() => {
                const pts = [];
                for (let a = 0; a <= 90; a += 10) {
                  const rad = (a * Math.PI) / 180;
                  pts.push(Math.sin(rad) * dw, -Math.cos(rad) * dw);
                }
                return pts;
              })()}
              stroke="#3b82f6"
              strokeWidth={1.5}
              dash={[4, 4]}
            />
            <Text text={`Porta ${Math.round(dw)}cm`} x={dw / 2 - 25} y={-dw / 2} fontSize={10} fontStyle="bold" fill="#64748b" />
          </Group>
        );

      case 'window':
        const ww = element.width || 100;
        const wh = element.height || 15;
        const wrot = element.rotation || 0;
        return (
          <Group key={element.id || index} x={element.x} y={element.y} rotation={wrot}>
            <Rect x={0} y={0} width={ww} height={wh} stroke="#0284c7" strokeWidth={1.5} fill="rgba(56, 189, 248, 0.12)" />
            <Line points={[0, wh * 0.35, ww, wh * 0.35]} stroke="#0369a1" strokeWidth={1.5} />
            <Line points={[0, wh * 0.65, ww, wh * 0.65]} stroke="#0369a1" strokeWidth={1.5} />
            <Line points={[0, 0, 0, wh]} stroke="#0f172a" strokeWidth={2} />
            <Line points={[ww, 0, ww, wh]} stroke="#0f172a" strokeWidth={2} />
            <Text text={`Janela ${Math.round(ww)}cm`} x={ww / 2 - 30} y={wh + 4} fontSize={10} fontStyle="bold" fill="#0284c7" />
          </Group>
        );

      case 'appliance':
        return (
          <ApplianceElement
            key={element.id || index}
            element={element}
            isSelected={false}
            draggable={false}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Stage
      width={width}
      height={height}
      style={{ backgroundColor: '#f9fafb' }}
    >
      {bgImage && (
        <Layer>
          <KonvaImage
            image={bgImage}
            x={0}
            y={0}
            width={width}
            height={height}
          />
        </Layer>
      )}
      
      <Layer>
        {allVisibleElements.map((element, index) => renderElement(element, index))}
      </Layer>
    </Stage>
  );
};

export default SketchCanvasViewer;
